import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PackRipReveal from "@/components/PackRipReveal";
import { playRareUnlockCry, pickBrightestRarity } from "@/utils/rareUnlockCry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpinWheel() {
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(null);
  const [result, setResult] = useState(null);
  const [dupeOffer, setDupeOffer] = useState(null);
  const [sellingDupe, setSellingDupe] = useState(false);
  // 🎁 Shared pack-rip modal — fires when a Spin lands on the 3-Card Draw.
  const [ccgReveal, setCcgReveal] = useState(null);
  // 🔥 Owner directive Feb 2026 — DFCL card wins ask the player each time:
  //    Use Now (opens the pack, lands the card in the binder) vs.
  //    Save for Later (keeps it in dfcl_unopened_packs).
  const [dfclChoice, setDfclChoice] = useState(null);
  const [openingPack, setOpeningPack] = useState(false);
  const wheelRef = useRef(null);
  const spinTimerRef = useRef(null);
  const safetyTimerRef = useRef(null);

  // Always release timers on unmount so the wheel can't stay stuck.
  useEffect(() => () => {
    if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
  }, []);

  async function loadState() {
    try {
      const { data } = await axios.get(`${API}/spin/state`, { withCredentials: true });
      setState(data);
    } catch (e) {
      // not authed → quietly hide
      setState(null);
    }
  }

  useEffect(() => { if (user) loadState(); }, [user]);

  if (!user || !state) return null;

  const slots = state.slots;
  const sliceAngle = 360 / slots.length;

  async function spin(useExtra = false) {
    if (spinning) return;
    if (useExtra) {
      if (!state.extra_spins || state.extra_spins <= 0) {
        toast.error("No 🎰 Extra Spin tokens available.");
        return;
      }
    } else {
      if (state.spins_left <= 0) {
        toast.error("Daily spin limit hit. Use an 🎰 Extra Spin token or come back tomorrow.");
        return;
      }
      if (state.balance < state.cost) {
        toast.error(`Need ${state.cost} Phoenix Tokens. You have ${state.balance}.`);
        return;
      }
    }
    setSpinning(true);
    setResult(null);
    // Safety: no matter what (network hang, throw, etc.), unblock after 8s.
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    safetyTimerRef.current = setTimeout(() => {
      setSpinning(false);
    }, 8000);
    try {
      const { data } = await axios.post(
        `${API}/spin`,
        { use_extra_spin: !!useExtra },
        { withCredentials: true },
      );
      // 🪄 Feb 2026 — Prize Grid picker. Instead of rotating a wheel we
      // run a chase highlight across the grid cells, then stop on the
      // winner. The timing roughly matches the old 4s wheel sweep.
      const winnerIdx = data.slot_index;
      const sweepTicks = 24;             // total steps across the grid
      const stepMs     = 70;             // ~1.7s sweep
      let tick = 0;
      let sweepTimer = null;
      const sweep = () => {
        tick += 1;
        setHighlightIdx((tick + winnerIdx) % slots.length);
        if (tick < sweepTicks) {
          sweepTimer = setTimeout(sweep, stepMs);
        } else {
          // Final: lock the highlight on the winner cell.
          setHighlightIdx(winnerIdx);
        }
      };
      sweep();
      // After animation ends (~sweepTicks * stepMs + buffer), show
      // toast + refresh state. Use the shared ref so unmount can still
      // cancel it, but kick it off at the correct offset so it runs
      // AFTER the sweep lands on the winner cell.
      if (spinTimerRef.current) clearTimeout(spinTimerRef.current);
      spinTimerRef.current = setTimeout(async () => {
        setResult(data);
        const net = data.net;
        if (data.streak_bonus_applied) {
          toast.success(`🔥 STREAK BONUS! ${data.slot.icon} ${data.slot.label} — guaranteed 50+ Phoenix Tokens!`, { duration: 5000 });
        } else if (data.used_extra_spin) {
          toast.success(`🎰 Extra Spin used! ${data.slot.icon} ${data.slot.label}`, { duration: 4000 });
        } else if (net > 0) {
          toast.success(`${data.slot.icon} ${data.slot.label} — net +${net} Phoenix Tokens!`, { duration: 4500 });
        } else if (data.payout > 0) {
          toast(`${data.slot.icon} ${data.slot.label} — net ${net} Phoenix Tokens.`, { duration: 3500 });
        } else if (!data.drop) {
          toast.error(`${data.slot.icon} ${data.slot.label} — better luck next spin.`, { duration: 3500 });
        }
        // Special drop handling (sticker / rename_card / omni_dollars / free_spin / perk_drop)
        if (data.drop?.kind === "sticker") {
          const st = data.drop.sticker;
          if (data.drop.duplicate) {
            // Open duplicate-sell modal (handled by state below)
            setDupeOffer({ ...data.drop, sticker: st });
          } else {
            toast.success(`${st.icon} New sticker: ${st.name}!`, {
              description: `Rarity: ${st.rarity}`,
              duration: 5500,
            });
          }
        } else if (data.drop?.kind === "exp_charge") {
          toast.success(`⚡ ${data.drop.name || "EXP Charge"} dropped!`, {
            description: `+${data.drop.exp_amount || 100} Hero-Class XP, stored in your inventory. Burn it from the Bag when you need a level push.`,
            duration: 6500,
          });
        } else if (data.drop?.kind === "soul_key_drop") {
          toast.success(`🗝️ Soul Key dropped!`, {
            description: "A Soul Key has been added to your stash. Use it to re-roll a hero class.",
            duration: 6000,
          });
        } else if (data.drop?.kind === "omni_dollars") {
          toast.success(`☯️ +${data.drop.amount} Omni Dollars!`, {
            description: "Spend them in the Shop on real merch.",
            duration: 5500,
          });
        } else if (data.drop?.kind === "perk_drop") {
          toast.success(`⚡ Perk dropped — ${data.drop.name}!`, {
            description: data.drop.description || "Check your Phoenix Lounge inventory.",
            duration: 6500,
          });
        } else if (data.drop?.kind === "bag_consumable") {
          toast.success(`${data.drop.icon || "🧪"} ${data.drop.name} ×${data.drop.count || 1} — Battle Bag drop!`, {
            description: `${data.drop.tier_meta?.label || data.drop.tier || ""} · ${data.drop.description || "Added to your Stat Sheet Battle Bag."}`,
            duration: 6500,
          });
        } else if (data.drop?.kind === "battle_bag") {
          // 🎒 Mar 4, 2026 — Consolidated Battle Bag sleeve. The sub_kind
          // tells us exactly which of the 4 legacy sub-slices actually
          // rolled so we can render the correct celebration copy.
          const sk = data.drop.sub_kind;
          const icon = data.drop.icon || "🎒";
          if (sk === "bag_consumable") {
            toast.success(`${icon} ${data.drop.name} ×${data.drop.count || 1} — Battle Bag!`, {
              description: `${data.drop.tier_meta?.label || data.drop.tier || ""} · Added to your Stat Sheet Battle Bag.`,
              duration: 6500,
            });
          } else if (sk === "nebula_vial") {
            toast.success(`${icon} ${data.drop.name} — Battle Bag!`, {
              description: `+${data.drop.granted_pts} ${(data.drop.stat || "").toUpperCase()} points banked in your Stat Sheet.`,
              duration: 6500,
            });
          } else if (sk === "stat_potion") {
            toast.success(`${icon} ${data.drop.name} — Battle Bag!`, {
              description: `+${data.drop.granted_pts} ${(data.drop.stat || "").toUpperCase()} points banked in your Stat Sheet.`,
              duration: 6500,
            });
          } else if (sk === "charisma_elixir") {
            toast.success(`${icon} ${data.drop.name} — Battle Bag!`, {
              description: `+${data.drop.granted_pts} Charisma banked. Silvertongue unlocks nearer.`,
              duration: 6500,
            });
          }
        } else if (data.drop?.kind === "friendship_hearts_bag") {
          // 💕 Feb 2026 — Random 1-20 Friendship Hearts (exact number
          // shown). Landing on the 20-cap posts to today_feed like a
          // JACKPOT.
          const isMax = data.drop.is_max;
          toast.success(
            `${isMax ? "🐦‍🔥 " : ""}💕 +${data.drop.amount} Friendship Heart${data.drop.amount === 1 ? "" : "s"}${isMax ? " — MAX ROLL!" : ""}`,
            {
              description: isMax
                ? `You rolled the 20-cap! Broadcasting to today's feed. Lifetime: ${(data.drop.hearts_total || 0).toLocaleString()} 💕`
                : `Lifetime: ${(data.drop.hearts_total || 0).toLocaleString()} 💕`,
              duration: isMax ? 8000 : 5500,
            },
          );
        } else if (data.drop?.kind === "bazaar_tickets_bag") {
          // 🎟️ Feb 2026 — Random 1-10 Bazaar Tickets (exact number shown).
          toast.success(
            `🎟️ +${data.drop.amount} Bazaar Ticket${data.drop.amount === 1 ? "" : "s"}!`,
            {
              description: `Spend them on Aethelgard Bazaar listings. Lifetime: ${(data.drop.tickets_total || 0).toLocaleString()} 🎟️`,
              duration: 5500,
            },
          );
        } else if (data.drop?.kind === "ccg_card_draw") {
          const cards = data.drop.cards || [];
          const dupRefund = Number(data.drop.dupe_refund_unbound_od || 0);
          if (cards.length > 0) {
            // 🐦‍🔥 Rare Unlock Cry — fire ONCE for the brightest rarity
            // in the draw (purple / orange / gold). No-op for lesser rolls.
            playRareUnlockCry(pickBrightestRarity(cards));
            setCcgReveal({
              pack: { icon: "🎴", name: "3-Card Draw" },
              cards,
              dupe_refund_unbound_od: dupRefund,
            });
          } else {
            toast.success("🎴 3-Card Draw — no cards rolled.", { duration: 4000 });
          }
        } else if (data.drop?.kind === "dfcl_card_drop") {
          // 🔥 Owner directive Feb 2026 — DFCL win is NOT auto-opened.
          // The pack lands in `dfcl_unopened_packs`. We pop a choice
          // modal: Use Now (opens the pack now) vs. Save for Later.
          // 🐦‍🔥 Rare Unlock Cry fires on the reveal moment (modal open).
          playRareUnlockCry(data.drop.rarity);
          setDfclChoice({
            card_id: data.drop.card_id,
            name:    data.drop.name,
            icon:    data.drop.icon || "🔥",
            rarity:  data.drop.rarity || "common",
            unopened_packs: data.drop.unopened_packs ?? 1,
          });
        } else if (data.drop?.kind === "luck_stones_bag") {
          // 🪨 Feb 2026 — 1-9 Luck Stones tile. Uniform random level.
          toast.success(
            `${data.drop.icon || "🪨"} Luck Stone — Lv ${data.drop.level}!`,
            {
              description: `Added to your Blacksmith stash (×${data.drop.qty || 1}). Rolled uniformly 1-9.`,
              duration: 5000,
            },
          );
        }
        // Spin-streak / sticker-set badge toasts
        if (data.new_badges && data.new_badges.length > 0) {
          data.new_badges.forEach((badgeId, idx) => {
            setTimeout(() => {
              if (badgeId === "chain-master") {
                toast.success(`🔥 Badge Unlocked — Chain Master!`, {
                  description: "You chained pity into power. First 4-in-a-row Extra Spin bonus triggered.",
                  duration: 6500,
                });
              } else {
                toast.success(`🏅 Badge Unlocked — ${badgeId}`, { duration: 5000 });
              }
            }, 600 + idx * 700);
          });
        }
        await loadState();
        await refreshUser();
        if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
        setSpinning(false);
      }, 4200);
    } catch (e) {
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
      toast.error(e?.response?.data?.detail || "Spin failed.");
      setSpinning(false);
    }
  }

  const SIZE = 320;
  const RADIUS = SIZE / 2;

  return (
    <div
      data-testid="spin-wheel"
      className="bg-[#0F0F0F] border border-[#D4AF37]/40 p-6 mt-6"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1 inline-flex items-center gap-1.5">
        <span>🎡</span> Phoenix Spin
      </div>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <h2 className="font-display text-3xl text-white">
          Pull the <span className="text-[#D4AF37]">Prize.</span>
        </h2>
        <div className="text-right">
          <div className="font-mono tabular-nums font-bold text-2xl text-[#D4AF37]" data-testid="spin-balance">
            {state.balance.toLocaleString()} <span className="text-xs text-neutral-500">Phoenix Tokens</span>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500" data-testid="spin-counter">
            {state.spins_left} of {state.max_per_day} spins left today
          </div>
          {state.streak && state.streak.current > 0 && (
            <div
              data-testid="spin-streak"
              className="text-[10px] uppercase tracking-widest text-orange-400 mt-1 inline-flex items-center gap-1"
            >
              <span>🔥</span> {state.streak.current}-day spin streak
              {state.streak.longest > state.streak.current && (
                <span className="text-neutral-600 normal-case tracking-normal">
                  (best {state.streak.longest})
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col items-center gap-5">
        {/* 🪄 Feb 2026 — Prize Grid picker replaces the old SVG wheel.
           Every active slice is its own tile: icon, label, exact drop %.
           Scales to unlimited prizes without text overlap. A chase
           highlight sweeps the grid while a spin resolves and locks on
           the winning tile.
        */}
        <div
          className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
          data-testid="spin-prize-grid"
        >
          {slots.map((s, i) => {
            const isJackpot = s.label === "JACKPOT";
            const kind = s.kind;
            const isWinner = !spinning && result && result.slot_index === i;
            const isLit    = highlightIdx === i;
            // Rarity accent per prize kind
            let accent;
            if (isJackpot) accent = "border-amber-400 bg-amber-400/15 text-amber-200";
            else if (kind === "omni_dollars")        accent = "border-emerald-700 bg-emerald-900/30 text-emerald-200";
            else if (kind === "perk_drop")           accent = "border-sky-700 bg-sky-900/30 text-sky-200";
            else if (kind === "battle_bag")          accent = "border-teal-700 bg-teal-900/30 text-teal-200";
            else if (kind === "ccg_card_draw")       accent = "border-amber-700 bg-amber-900/30 text-amber-200";
            else if (kind === "dfcl_card_drop")      accent = "border-rose-700 bg-rose-900/30 text-rose-200";
            else if (kind === "friendship_hearts_bag") accent = "border-pink-700 bg-pink-900/30 text-pink-200";
            else if (kind === "bazaar_tickets_bag")  accent = "border-fuchsia-700 bg-fuchsia-900/30 text-fuchsia-200";
            else if (kind === "luck_stones_bag")     accent = "border-stone-500 bg-stone-800/60 text-stone-200";
            else accent = "border-violet-700 bg-violet-900/30 text-violet-200";
            const stateCls = isWinner
              ? "ring-2 ring-amber-300 shadow-[0_0_24px_rgba(251,191,36,0.55)] scale-[1.02]"
              : isLit
                ? "ring-2 ring-amber-200 bg-amber-500/20 scale-[1.03]"
                : "";
            // 🪄 Feb 2026 — gold shimmer for the top-tier prizes (Jackpot
            // + DFCL card). Pure CSS overlay so it costs nothing to render
            // and never conflicts with the chase highlight.
            const isTopTier = isJackpot || kind === "dfcl_card_drop";
            return (
              <div
                key={i}
                data-testid={`spin-prize-${i}`}
                data-prize-kind={kind || "pt"}
                className={`relative overflow-hidden border p-2.5 text-center transition-all duration-100 ${accent} ${stateCls}`}
              >
                {isTopTier && (
                  <div
                    aria-hidden
                    data-testid={`spin-prize-shimmer-${i}`}
                    className="pointer-events-none absolute inset-0 prize-shimmer"
                  />
                )}
                <div className="relative text-3xl leading-none mb-1" aria-hidden>{s.icon}</div>
                <div className="relative text-[11px] font-bold uppercase tracking-wider leading-tight line-clamp-2 min-h-[26px]">
                  {s.label}
                </div>
                <div
                  className="relative mt-1 text-[10px] font-mono font-black tabular-nums text-amber-300"
                  data-testid={`spin-prize-pct-${i}`}
                  title="Drop rate — your exact chance to land here per spin"
                >
                  {typeof s.drop_pct === "number" ? `${s.drop_pct.toFixed(2)}%` : "—"}
                </div>
                {isJackpot && (
                  <div className="absolute -top-1.5 -right-1.5 text-[9px] font-black uppercase bg-amber-300 text-black px-1 py-0.5 rounded z-10">
                    Top
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Spin button + Extra Spin (Jul 2026 wiring) */}
        <div className="flex flex-col items-center gap-2">
          <button
            data-testid="spin-button"
            onClick={() => spin(false)}
            disabled={spinning || state.spins_left <= 0 || state.balance < state.cost}
            className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-sm px-8 py-4 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {spinning
              ? "Pulling..."
              : state.spins_left <= 0
              ? "Come Back Tomorrow"
              : state.balance < state.cost
              ? `Need ${state.cost} Phoenix Tokens`
              : `Pull · ${state.cost} Phoenix Tokens`}
          </button>

          {state.extra_spins > 0 && (
            <button
              data-testid="spin-extra-button"
              onClick={() => spin(true)}
              disabled={spinning}
              className="bg-purple-600/80 hover:bg-purple-500 text-white font-bold uppercase tracking-[0.15em] text-[11px] px-5 py-2 rounded-md border border-purple-400/60 shadow-[0_0_18px_rgba(168,85,247,0.35)] disabled:opacity-40 disabled:cursor-not-allowed"
              title="Free spin — bypasses Phoenix Tokens cost and daily cap"
            >
              🎰 Use Extra Spin · {state.extra_spins} left
            </button>
          )}
          {state.extra_spins > 0 && state.spin_streak_extras >= 1 && (
            <div
              className="text-[10px] uppercase tracking-widest text-purple-300 flex items-center gap-1"
              data-testid="spin-streak-counter"
              title={`Chain ${state.spin_streak_extras}/4 — hit 4 for a guaranteed 50+ Phoenix Tokens roll.`}
            >
              🔥 Streak: {state.spin_streak_extras}/{state.streak_bonus_at || 4}
              {state.spin_streak_extras >= (state.streak_bonus_at || 4) && (
                <span className="text-amber-300"> · BONUS READY</span>
              )}
            </div>
          )}
        </div>

        {result && !spinning && (
          <div
            data-testid="spin-result"
            className="text-center"
          >
            <div className="text-3xl mb-1">{result.slot.icon}</div>
            <div className="font-display text-lg text-white">{result.slot.label}</div>
            <div className={`text-xs font-bold uppercase tracking-widest ${
              result.net > 0 ? "text-green-400" : result.net === 0 ? "text-neutral-400" : "text-red-400"
            }`}>
              Net {result.net > 0 ? "+" : ""}{result.net} Phoenix Tokens
            </div>
          </div>
        )}
      </div>

      <p className="text-[10px] text-neutral-600 uppercase tracking-widest mt-5 text-center">
        Spend {state.cost} Phoenix Tokens · Each tile shows its exact drop % · Max {state.max_per_day}/day
      </p>

      {/* Duplicate sticker auto-sell modal */}
      {dupeOffer && (
        <div
          data-testid="dupe-sell-modal"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !sellingDupe && setDupeOffer(null)}
        >
          <div
            className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-sm w-full p-6 text-center relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-2">
              Duplicate Sticker
            </div>
            <div className="text-7xl my-4 leading-none">{dupeOffer.sticker?.icon}</div>
            <h3 className="font-display text-2xl text-white mb-1">{dupeOffer.sticker?.name}</h3>
            <p className="text-xs uppercase tracking-widest text-neutral-500 mb-4">
              You already own this one
            </p>
            <p className="text-sm text-neutral-300 mb-5 leading-relaxed">
              Sell this duplicate for <span className="text-[#D4AF37] font-bold">{dupeOffer.auto_sell_pt} Phoenix Tokens</span>?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="dupe-keep"
                onClick={() => setDupeOffer(null)}
                disabled={sellingDupe}
                className="py-3 border border-zinc-800 text-neutral-400 text-xs uppercase tracking-widest font-bold hover:text-white hover:border-neutral-600 disabled:opacity-40"
              >
                Keep
              </button>
              <button
                data-testid="dupe-sell"
                disabled={sellingDupe}
                onClick={async () => {
                  setSellingDupe(true);
                  try {
                    const { data } = await axios.post(`${API}/stickers/sell-duplicate`, {
                      sticker_id: dupeOffer.sticker_id,
                    });
                    toast.success(`🐦‍🔥 +${data.credited_pt} Phoenix Tokens credited!`, { duration: 4000 });
                    await loadState();
                    await refreshUser?.();
                    setDupeOffer(null);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Sell failed.");
                  } finally {
                    setSellingDupe(false);
                  }
                }}
                className="py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-widest font-bold hover:bg-[#F1C84B] disabled:opacity-40"
              >
                {sellingDupe ? "Selling..." : `Sell · +${dupeOffer.auto_sell_pt} Phoenix Tokens`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🎴 3-Card Draw reveal — same rip-and-flip cinematic as pack buys. */}
      <PackRipReveal result={ccgReveal} onClose={() => setCcgReveal(null)} />

      {/* 🔥 DFCL win — Use Now vs. Save for Later (owner directive Feb 2026) */}
      {dfclChoice && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="dfcl-choice-modal"
        >
          <div className="max-w-sm w-full bg-[#0F0A08] border-2 border-rose-700 p-5 text-center">
            <div className="text-[10px] uppercase tracking-[0.35em] text-rose-300 mb-2">
              DFCL Card Won
            </div>
            <div className="text-6xl mb-2">{dfclChoice.icon}</div>
            <div className="font-display text-2xl font-black text-white mb-1">
              {dfclChoice.name}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-rose-300/70 mb-4">
              Rarity: {String(dfclChoice.rarity).toUpperCase()}
            </div>
            <div className="text-[12px] text-neutral-300 mb-4">
              This pack is sealed. Open it now to add the card to your binder,
              or save it for later.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="dfcl-use-now-btn"
                disabled={openingPack}
                onClick={async () => {
                  if (openingPack) return;
                  setOpeningPack(true);
                  try {
                    const r = await axios.post(
                      `${API}/dfcl/binder/open-pack`,
                      { card_id: dfclChoice.card_id },
                      { withCredentials: true },
                    );
                    const d = r.data || {};
                    toast.success(
                      `${dfclChoice.icon} ${d.is_first_unlock ? "NEW " : ""}${d.card_name}!`,
                      {
                        description: d.is_first_unlock
                          ? `Added to your binder. Rarity: ${String(d.rarity).toUpperCase()}.`
                          : `+1 copy added (×${d.copies}). Rarity: ${String(d.rarity).toUpperCase()}.`,
                        duration: 6500,
                      },
                    );
                    setDfclChoice(null);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not open the pack.");
                  } finally {
                    setOpeningPack(false);
                  }
                }}
                className="py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-widest font-bold hover:bg-[#F1C84B] disabled:opacity-50"
              >
                {openingPack ? "Opening…" : "Use Now"}
              </button>
              <button
                data-testid="dfcl-save-later-btn"
                disabled={openingPack}
                onClick={() => {
                  toast.success("📦 Saved to your binder's Unopened Packs.", {
                    description: `${dfclChoice.name} will wait for you — open it anytime.`,
                    duration: 5500,
                  });
                  setDfclChoice(null);
                }}
                className="py-3 bg-zinc-800 border border-zinc-700 text-neutral-200 text-xs uppercase tracking-widest font-bold hover:bg-zinc-700 disabled:opacity-50"
              >
                Save for Later
              </button>
            </div>
            {dfclChoice.unopened_packs > 1 && (
              <div className="mt-2 text-[10px] text-neutral-500 italic">
                You now have {dfclChoice.unopened_packs} unopened pack{dfclChoice.unopened_packs === 1 ? "" : "s"} for this card.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
